import React from 'react';
import { useImpressionRef } from '@noths/polaris-client-react-hooks';
import { getAnchorElementFromEvent } from '@noths/polaris-client-utils';

import { Banner } from '../../elements/Banner/Banner';
import { FloatingBannerImage } from '../../elements/Banner/FloatingBannerImage/FloatingBannerImage';
import { Heading } from '../../internal/Heading/Heading';
import type { HeadingInnerProps, HeroBannerProps, UnderlineColorCSSProperties } from './types';
import * as styles from './HeroBanner.styles';
import { SolidButton, SolidButtonLink } from 'client-ribbons-design-system';

const HeadingInner = ({ children, hasUnderline }: HeadingInnerProps) => (
  <span css={styles.innerHeading}>
    <span css={hasUnderline ? styles.innerHeadingUnderline : undefined}>{children}</span>
  </span>
);

export const HeroBanner = ({
  backgroundColor,
  buttonBackgroundColor,
  buttonDisabled = false,
  buttonHref,
  buttonText,
  buttonTextColorMode = 'light',
  copy,
  headingIsPageTitle,
  headingLine1,
  headingLine2,
  headingUnderlineColor,
  imageAlt,
  imageHeight,
  imageLinkHref,
  imageWidth,
  images,
  lazyImages,
  onButtonClick,
  onImageLinkClick,
  onLinkClick,
  onVisible = () => {},
  subHeading,
  textColorMode,
}: HeroBannerProps) => {
  const observedElementRef = useImpressionRef<HTMLDivElement>(() => onVisible());
  const hasUnderline = !!headingUnderlineColor;
  const removeImageLinkFromTabIndex = imageLinkHref === buttonHref;
  const commonButtonProps = {
    customBackgroundColor: buttonBackgroundColor,
    onClick: onButtonClick,
    textColorMode: buttonTextColorMode,
  };

  const handleTextClick = (e: React.MouseEvent) => {
    if (onLinkClick && getAnchorElementFromEvent(e)) {
      onLinkClick(e as React.MouseEvent<HTMLAnchorElement>);
    }
  };

  const Image = () => (
    <FloatingBannerImage
      imageAlt={imageAlt}
      imageHeight={imageHeight}
      imageWidth={imageWidth}
      images={images}
      lazyImages={lazyImages}
      offset="top"
    />
  );

  return (
    <div css={styles.topOffsetImageMarginContainer} ref={observedElementRef}>
      <Banner backgroundColor={backgroundColor}>
        <div css={styles.container}>
          {imageLinkHref ? (
            <a
              css={styles.imageWrapper}
              href={imageLinkHref}
              onClick={onImageLinkClick}
              {...(removeImageLinkFromTabIndex ? { tabIndex: -1 } : {})}
            >
              <Image />
            </a>
          ) : (
            <div css={styles.imageWrapper}>
              <Image />
            </div>
          )}
          <div css={styles.content}>
            <div css={textColorMode === 'light' ? styles.lightTextContent : styles.darkTextContent}>
              <Heading
                css={[
                  styles.heading,
                  buttonText ? styles.headingWithButton : styles.headingWithoutButton,
                ]}
                level={headingIsPageTitle ? 1 : 2}
                style={
                  {
                    '--underlineColor': headingUnderlineColor,
                  } as UnderlineColorCSSProperties
                }
              >
                <HeadingInner hasUnderline={hasUnderline}>{headingLine1}</HeadingInner>
                {headingLine2 && (
                  <HeadingInner hasUnderline={hasUnderline}>{headingLine2}</HeadingInner>
                )}
              </Heading>
              {subHeading && (
                // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
                <div
                  css={[
                    styles.subHeading,
                    buttonText ? styles.subHeadingWithButton : styles.subHeadingWithoutButton,
                  ]}
                  onClick={handleTextClick}
                >
                  {subHeading}
                </div>
              )}
              {copy && (
                // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
                <div css={styles.copy} onClick={handleTextClick}>
                  {copy}
                </div>
              )}
            </div>
            {buttonText && (
              <div css={styles.buttonContainer}>
                {buttonHref ? (
                  <SolidButtonLink {...commonButtonProps} href={buttonHref}>
                    {buttonText}
                  </SolidButtonLink>
                ) : (
                  <SolidButton {...commonButtonProps} disabled={buttonDisabled}>
                    {buttonText}
                  </SolidButton>
                )}
              </div>
            )}
          </div>
        </div>
      </Banner>
    </div>
  );
};
